import React from "react";
import { useTheme } from "styled-components";
import { formatCurrency } from "@wrstudios/utils";
import {
  getAverageAdjustedPrice,
  getListingByHighestAdjustedPrice,
  getListingByLowestAdjustedPrice
} from "../../../utils/listings";
import {
  Container,
  ContainerInner,
  Graph,
  Bars,
  Bar,
  Legends,
  Legend,
  LegendValue,
  Value,
  Label
} from "./styled/live-average-pricing-chart";

function LiveAveragePricingChart({
  listings,
  hoverListingId,
  setHoverListingId,
  setPreviewListingId
}) {
  const theme = useTheme();
  const highestListing = getListingByHighestAdjustedPrice(listings);
  const lowestListing = getListingByLowestAdjustedPrice(listings);
  const averageListingPrice = getAverageAdjustedPrice(listings);
  const hasSingleListing = listings.length === 1;
  const showHighAndLowLegend = highestListing.price !== lowestListing.price;
  const colorSpectrum = theme.getColorSpectrumFromColor(
    lowestListing.statusColor
  );

  return (
    <Container>
      <ContainerInner>
        <Graph>
          <Legends>
            <Legend
              isHidden={!showHighAndLowLegend}
              aria-hidden={!showHighAndLowLegend}>
              <LegendValue>
                <Value>{formatCurrency(highestListing.adjustedPrice)}</Value>{" "}
                <Label>Highest</Label>
              </LegendValue>
            </Legend>
            <Legend color={colorSpectrum[200]}>
              <LegendValue isAverage>
                <Value color={colorSpectrum[600]} isAverage>
                  {formatCurrency(averageListingPrice)}
                </Value>{" "}
              </LegendValue>
              <LegendValue isBelow>
                <Label>Average</Label>
              </LegendValue>
            </Legend>
            <Legend
              isHidden={!showHighAndLowLegend}
              aria-hidden={!showHighAndLowLegend}>
              <LegendValue isBelow>
                <Value>{formatCurrency(lowestListing.adjustedPrice)}</Value>{" "}
                <Label>Lowest</Label>
              </LegendValue>
            </Legend>
          </Legends>
          <Bars>
            {listings.map((listing, index) => {
              const isFirst = index === 0;
              const isLowest = listing.recordId === lowestListing.recordId;
              const isHighest = listing.recordId === highestListing.recordId;
              const isWithinBounds = !isLowest && !isHighest;
              const isHovering = listing.recordId === hoverListingId;

              // prettier-ignore
              const averagePercentageOfRange =
                ((averageListingPrice - lowestListing.adjustedPrice) /
                  (highestListing.adjustedPrice - lowestListing.adjustedPrice)) * 100

              const differenceOfAveragePriceFromCenter =
                averagePercentageOfRange - 50;

              // prettier-ignore
              let listingValuePercentageOfRange =
                ((listing.adjustedPrice - lowestListing.adjustedPrice) /
                  (highestListing.adjustedPrice - lowestListing.adjustedPrice)) * 100 - (isWithinBounds ? differenceOfAveragePriceFromCenter : 0)

              if (listingValuePercentageOfRange > 100) {
                listingValuePercentageOfRange = 100;
              }

              if (listingValuePercentageOfRange < 0) {
                listingValuePercentageOfRange = 0;
              }

              return (
                <Bar
                  key={listing.recordId}
                  style={{
                    height: hasSingleListing
                      ? "calc(69% + 1.8rem)"
                      : isLowest
                      ? `calc(${listingValuePercentageOfRange}% + 3.6rem)`
                      : `calc(${listingValuePercentageOfRange}% + 3.4rem)`,
                    marginLeft: isFirst
                      ? 0
                      : listings.length < 10
                      ? "8%"
                      : listings.length < 15
                      ? "4%"
                      : "2%"
                  }}
                  isGhost={listing.isGhost}
                  isHovering={isHovering}
                  statusColor={listing.statusColor}
                  onMouseEnter={() => setHoverListingId(listing.recordId)}
                  onMouseLeave={() => setHoverListingId(null)}
                  onClick={() => setPreviewListingId(listing.recordId)}
                />
              );
            })}
          </Bars>
        </Graph>
      </ContainerInner>
    </Container>
  );
}

export default LiveAveragePricingChart;
